import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { MaterialProductSelection, MaterialShapeSelection, MaterialTypeSelection } from '@steelbuy/ui-domain';
import {
    FormItem,
    InputRadioButton,
    InputRadioButtonGroup,
    InputRadioButtonGroupAlign,
    MarketingBannerAdvantages,
    Notification,
    NotificationLevel,
    useWizardStepsContext,
    WizardBarItemStatus,
} from '@steelbuy/ui-primitive';
import { FormLayout } from '../../../views/layouts/form-layout/FormLayout';
import { WizardActionBar } from '../../wizard-action-bar/WizardActionBar';
import { BulkListingStep } from '../BulkListing.enums';
import { getStepRoute } from '../bulkListingUtil';

import './ChooseMaterialStep.scss';

export const ChooseMaterialStep = () => {
    const { draftId = '' } = useParams();
    const { t } = useTranslation(['translation', 'domainModel']);
    const navigate = useNavigate();
    const { updateStepStatus } = useWizardStepsContext();
    const { control, watch } = useFormContext();

    return (
        <FormLayout>
            <p className="bulk-listing-import-listing__title">
                {t('translation:application.createBulkListing.chooseMaterial.header')}
            </p>
            <Controller
                control={control}
                render={({ field: { value, onChange } }) => (
                    <MaterialTypeSelection
                        materialType={value}
                        materialTypeSelected={(materialType) => onChange(materialType)}
                        isStainlessFeatureEnabled
                    />
                )}
                name="materialType"
            />
            <Controller
                control={control}
                render={({ field: { value, onChange } }) => (
                    <MaterialShapeSelection
                        material={{
                            materialType: watch('materialType'),
                            shape: value,
                        }}
                        shapeSelected={(shape) => onChange(shape)}
                    />
                )}
                name="shape"
            />
            <div className="bulk-listing-choose-material-product-selection">
                <Controller
                    control={control}
                    render={({ field: { value, onChange } }) => (
                        <MaterialProductSelection
                            material={{
                                materialType: watch('materialType'),
                                shape: watch('shape'),
                                product: value,
                            }}
                            productSelected={(product) => onChange(product)}
                            isPackagesSupported={false}
                        />
                    )}
                    name="product"
                />
                <Notification
                    level={NotificationLevel.INFO}
                    message={t('translation:application.materialStep.missingProductsNotification')}
                    labelActionButton={t('translation:application.materialStep.missingProductsLinkLabel')}
                    onClickActionButton={() => {
                        const subject = t('translation:application.materialStep.missingProductsEmailSubject');
                        window.location.href = `mailto:support@steel-buy.com?subject=${encodeURIComponent(subject)}`;
                    }}
                />
            </div>
            <Controller
                control={control}
                render={({ field: { value, onChange } }) => (
                    <FormItem header={t('domainModel:material.quality.name')} isMandatory>
                        <InputRadioButtonGroup align={InputRadioButtonGroupAlign.ALIGN_HORIZONTAL}>
                            <InputRadioButton
                                name="prime"
                                value={t(`domainModel:material.quality.value.prime`)}
                                onChange={() => onChange(true)}
                                checked={value}
                            />
                            <InputRadioButton
                                name="prime"
                                value={t(`domainModel:material.quality.value.nonPrime`)}
                                onChange={() => onChange(false)}
                                checked={!value}
                            />
                        </InputRadioButtonGroup>
                    </FormItem>
                )}
                name="prime"
            />
            <MarketingBannerAdvantages
                header={t('translation:application.materialStep.anonymityBannerHeader')}
                text={t('translation:application.materialStep.anonymityBannerText')}
            />
            <WizardActionBar
                onNext={() => {
                    updateStepStatus(BulkListingStep.CHOOSE_MATERIAL, WizardBarItemStatus.SUCCESS);
                    navigate(getStepRoute(2, draftId));
                }}
                isSubmitDisabled={false}
                nextLabel={t('translation:application.createRFQ.wizardNextButtonLabel')}
                isLoading={false}
            />
        </FormLayout>
    );
};
